import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trackPublicFunnelEvent } from "@/lib/publicFunnel";
import type { OperatorIntakeResult } from "@/lib/operatorIntake";
import { UpdatesSignupCard, type UpdatesTrackKey } from "@/components/public/UpdatesSignup";

const POPUP_PLACEMENT = "newsletter_popup";
const DISMISSED_AT_KEY = "svm-newsletter-popup-dismissed-at";
const CONVERTED_AT_KEY = "svm-newsletter-popup-converted-at";
const SEEN_SESSION_KEY = "svm-newsletter-popup-seen-session";
const VISITED_PATHS_SESSION_KEY = "svm-newsletter-popup-visited-paths";
const DISMISS_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const CONVERSION_SNOOZE_MS = 90 * 24 * 60 * 60 * 1000;
const SCROLL_DEPTH_THRESHOLD = 0.45;
const SECOND_PAGE_DELAY_MS = 1200;

type PopupTrigger = "forced" | "scroll_depth" | "exit_intent" | "second_page";

const eligiblePaths = new Set([
  "/",
  "/ai-boardroom",
  "/ai-operators",
  "/app-build",
  "/systems-audit",
  "/app-rescue",
  "/project-planner",
  "/venture-evaluation",
  "/work",
  "/about",
  "/contact",
]);

const topicByPath: Partial<Record<string, UpdatesTrackKey>> = {
  "/ai-boardroom": "boardroom",
  "/app-rescue": "appRescue",
  "/app-build": "appBuild",
  "/ai-operators": "aiOperator",
  "/systems-audit": "systemsAudit",
  "/venture-evaluation": "ventureEvaluation",
};

function readStorageTimestamp(key: string): number | null {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return null;

    const timestamp = Number.parseInt(value, 10);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
}

function writeLocalTimestamp(key: string): void {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}

function hasRecentLocalTimestamp(key: string, ttlMs: number): boolean {
  const timestamp = readStorageTimestamp(key);
  return Boolean(timestamp && Date.now() - timestamp < ttlMs);
}

function hasSeenThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(SEEN_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

function markSeenThisSession(): void {
  try {
    window.sessionStorage.setItem(SEEN_SESSION_KEY, "true");
  } catch {
    // Session storage is a convenience, not a requirement for signup.
  }
}

function recordEligiblePathVisit(pathname: string): number {
  try {
    const rawValue = window.sessionStorage.getItem(VISITED_PATHS_SESSION_KEY);
    const visitedPaths = rawValue ? JSON.parse(rawValue) : [];
    const uniquePaths = Array.isArray(visitedPaths)
      ? visitedPaths.filter((path): path is string => typeof path === "string")
      : [];
    const nextPaths = uniquePaths.includes(pathname) ? uniquePaths : [...uniquePaths, pathname];

    window.sessionStorage.setItem(VISITED_PATHS_SESSION_KEY, JSON.stringify(nextPaths));
    return nextPaths.length;
  } catch {
    return 1;
  }
}

function topicForPath(pathname: string): UpdatesTrackKey {
  return topicByPath[pathname] || "general";
}

function canShowPopup(pathname: string): boolean {
  return eligiblePaths.has(pathname);
}

export default function NewsletterPopupInvite() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [converted, setConverted] = useState(false);
  const hasTrackedView = useRef(false);
  const pathname = location.pathname;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const forced = searchParams.get("svm_newsletter_popup") === "1";
  const suppressed = searchParams.get("svm_newsletter_popup") === "0";
  const defaultTopic = topicForPath(pathname);

  useEffect(() => {
    if (suppressed || !canShowPopup(pathname)) {
      setOpen(false);
      return;
    }

    if (
      !forced &&
      (hasSeenThisSession() ||
        hasRecentLocalTimestamp(DISMISSED_AT_KEY, DISMISS_SNOOZE_MS) ||
        hasRecentLocalTimestamp(CONVERTED_AT_KEY, CONVERSION_SNOOZE_MS))
    ) {
      return;
    }

    const visitedPathCount = recordEligiblePathVisit(pathname);
    let hasOpened = false;
    let timeoutId: number | undefined;

    const showPopup = (trigger: PopupTrigger) => {
      if (hasOpened) return;

      hasOpened = true;
      markSeenThisSession();
      setOpen(true);

      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        trackPublicFunnelEvent("updates_popup_viewed", {
          placement: POPUP_PLACEMENT,
          route: pathname,
          updates_topic: defaultTopic,
          forced,
          popup_trigger: trigger,
        });
      }
    };

    if (forced) {
      timeoutId = window.setTimeout(() => showPopup("forced"), 0);
      return () => window.clearTimeout(timeoutId);
    }

    if (visitedPathCount >= 2) {
      timeoutId = window.setTimeout(() => showPopup("second_page"), SECOND_PAGE_DELAY_MS);
      return () => window.clearTimeout(timeoutId);
    }

    const handleScroll = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;

      if (window.scrollY / scrollableHeight >= SCROLL_DEPTH_THRESHOLD) {
        showPopup("scroll_depth");
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget || event.clientY > 0) return;
      showPopup("exit_intent");
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mouseout", handleMouseOut);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mouseout", handleMouseOut);
    };
  }, [defaultTopic, forced, pathname, suppressed]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen && !converted) {
      writeLocalTimestamp(DISMISSED_AT_KEY);
      trackPublicFunnelEvent("updates_popup_dismissed", {
        placement: POPUP_PLACEMENT,
        route: pathname,
        updates_topic: defaultTopic,
      });
    }
  };

  const handleSubmitted = (result: OperatorIntakeResult) => {
    setConverted(true);
    writeLocalTimestamp(CONVERTED_AT_KEY);
    trackPublicFunnelEvent("updates_popup_converted", {
      placement: POPUP_PLACEMENT,
      route: pathname,
      updates_topic: defaultTopic,
      mode: result.mode,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-200 bg-white p-0 text-slate-950 shadow-[0_30px_90px_rgba(2,6,23,0.36)] sm:max-w-xl">
        <div className="grid gap-5 p-5 sm:p-6">
          <DialogHeader className="space-y-3 text-left">
            <p className="inline-flex w-fit rounded-full border border-amber-300/40 bg-amber-100 px-3 py-1 text-[0.68rem] font-black uppercase tracking-[0.18em] text-amber-900">
              SVM newsletter
            </p>
            <DialogTitle className="svm-display-tight text-3xl leading-tight text-slate-950 sm:text-4xl">
              Want the notes before you need the form?
            </DialogTitle>
            <DialogDescription className="text-sm font-medium leading-6 text-slate-600">
              Pick the track you care about. We’ll send practical launch notes while we prepare the next public
              material. No credentials, no spam.
            </DialogDescription>
          </DialogHeader>

          <UpdatesSignupCard
            placement={POPUP_PLACEMENT}
            variant="modal"
            defaultTopic={defaultTopic}
            submitLabel="Send me the notes"
            successTitle="You're on the SVM notes list."
            onSubmitted={handleSubmitted}
          />

          <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4 text-xs font-semibold text-slate-500">
            <span>Short, useful, launch-oriented updates only.</span>
            <DialogClose asChild>
              <Button variant="ghost" size="sm" className="shrink-0 text-slate-600 hover:text-slate-950">
                Not right now
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
